import { TrashIcon } from "@heroicons/react/20/solid";
import { useMemo, useState, useEffect, useCallback, memo } from "react";
import SingleSelectDropdown from "../ui/dropdown/SingleSelectDropdown";
import BooleanFilter from "./BooleanFilter";
import DateFilter from "./DateFilter";
import NumberFilter from "./NumberFilter";
import StringFilter from "./StringFilter";
import MemberFilter from "./MemberFilter";

type Props = {
  onRemove: (currentKey: number, filterId?: number) => void;
  currentKey: number;
  initField: any;
  initOps: any;
  initValues: any;
  traits: any[];
  successMetrics: any[];
  filterId: number;
  entity?: string;
  onCompleteFilter: (
    field: any,
    operator: any,
    value: any,
    type: any,
    entityType: any,
    id: number,
    filterId: number,
  ) => void;
};

const FilterRow = memo(({
  initField,
  initOps,
  initValues,
  entity,
  onRemove,
  traits,
  successMetrics,
  currentKey,
  onCompleteFilter,
  filterId,
}: Props) => {
  const defaultField = useMemo(() => ({
    name: "", id: -1, entity_type: "column", data_type: "string",
  }), []);

  const [field, setField] = useState<any>(initField || defaultField);

  // Sync field state with initField prop changes
  useEffect(() => {
    if (initField) {
      // Only update if the field actually changed to avoid unnecessary updates
      if (
        initField.name !== field?.name ||
        initField.attribute_type !== field?.attribute_type ||
        initField.data_type !== field?.data_type
      ) {
        setField(initField);
      }
    } else if (field?.name) {
      // If initField is cleared, reset field
      setField(defaultField);
    }
  }, [initField, field?.name, field?.attribute_type, field?.data_type, defaultField]);

  // Memoize onCompleteRow callback to prevent child re-renders
  const onCompleteRow = useCallback((
    operator: any,
    value: any,
    type: any,
    entityType: any,
  ) => {
    onCompleteFilter(
      field.name,
      operator,
      value,
      type,
      entityType,
      currentKey,
      filterId,
    );
  }, [field?.name, onCompleteFilter, currentKey, filterId]);

  // Memoize handleRemove callback
  const handleRemoveClick = useCallback(() => {
    onRemove(currentKey, filterId);
  }, [onRemove, currentKey, filterId]);

  // Memoize field change handler
  const handleFieldChange = useCallback((newField: any) => {
    setField(newField);
  }, []);

  // Memoize filter value check
  const isFieldMatching = useMemo(
    () => field?.name === initField?.name,
    [field?.name, initField?.name]
  );

  // Memoize renderFilter to prevent unnecessary recalculations
  const renderFilter = useMemo(() => {
    if (!field?.name) {
      return null;
    }

    const commonProps = {
      fieldSelect: !field?.name,
      handleChange: onCompleteRow,
      op: initOps,
      value: isFieldMatching ? initValues : undefined,
    };

    // Computed traits are always numeric columns — render as NumberFilter
    if (field?.generation_type === "computed_trait") {
      return (
        <NumberFilter
          key={`computed-trait-${field?.name}-${filterId}`}
          fieldSelect={commonProps.fieldSelect}
          handleChange={commonProps.handleChange}
          op={commonProps.op}
          value={commonProps.value}
        />
      );
    }

    if (
      field.name === "accounts.csm_id" ||
      field.name === "users.csm_id" ||
      field.name === "accounts.ae_id" ||
      field.name === "users.ae_id"
    ) {
      return (
        <MemberFilter
          key={`member-${field?.name}-${filterId}`}
          fieldSelect={commonProps.fieldSelect}
          handleChange={commonProps.handleChange}
          op={commonProps.op}
          value={commonProps.value}
        />
      );
    }

    if (field?.data_type === "int" || field?.data_type === "float") {
      return (
        <NumberFilter
          key={`number-${field?.name}-${filterId}`}
          fieldSelect={commonProps.fieldSelect}
          handleChange={commonProps.handleChange}
          op={commonProps.op}
          value={commonProps.value}
        />
      );
    }

    if (field?.data_type === "timestamp" || field?.data_type === "datetime") {
      return (
        <DateFilter
          key={`date-${field?.name}-${filterId}`}
          fieldSelect={commonProps.fieldSelect}
          handleChange={commonProps.handleChange}
          op={commonProps.op}
          value={commonProps.value}
        />
      );
    }

    if (field?.data_type === "boolean") {
      return (
        <BooleanFilter
          key={`boolean-${field?.name}-${filterId}`}
          fieldSelect={commonProps.fieldSelect}
          handleChange={commonProps.handleChange}
          op={commonProps.op}
          value={commonProps.value}
        />
      );
    }

    return (
      <StringFilter
        key={`string-${field?.name}-${filterId}`}
        fieldSelect={commonProps.fieldSelect}
        handleChange={commonProps.handleChange}
        op={commonProps.op}
        value={commonProps.value}
        selectedField={field?.name}
      />
    );
  }, [
    field?.name,
    field?.generation_type,
    field?.data_type,
    filterId,
    isFieldMatching,
    initOps,
    initValues,
    onCompleteRow,
  ]);

  return (
    <div className="flex gap-2 items-center">
      <SingleSelectDropdown
        list={traits}
        type={entity}
        successMetricsList={successMetrics}
        selected={field}
        setSelected={handleFieldChange}
        position="bottom-start"
      />
      {renderFilter}
      <button
        type="button"
        onClick={handleRemoveClick}
        className="inline-flex mt-1 h-7 w-7 items-center text-gray-500 hover:text-purple-500"
      >
        <span className="sr-only">close</span>
        <TrashIcon className="h-5 w-5" aria-hidden="true" />
      </button>
    </div>
  );
});

FilterRow.displayName = "FilterRow";

export default FilterRow;
